<script lang="ts" setup>
import OmniTextField from "@/components/omni/OmniTextField.vue";
import type { TripRouteFreight, TripRouteStop } from "@/types/trip";
import { useFreights } from "@/composables";
import { useValidation } from "@/composables/useValidation";
import { computed, ref, watch } from "vue";
import { OrderFreightState, OrderStopType } from "@/constants/order";
import RouteFreightState from "@/views/trips/components/RouteFreightState.vue";
import DeletedChip from "@/components/chips/DeletedChip.vue";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import { useRoute } from "vue-router";

const { freightTypePallets, freightTypePieces } = useFreights();
const { requiredValidator, positiveNumber } = useValidation();
const route = useRoute();

const routeStopFreights = defineModel<TripRouteFreight[]>({ default: [] });

const props = withDefaults(
  defineProps<{
    routeFreight: TripRouteFreight;
    routeStop: TripRouteStop;
    freightsFromApi: TripRouteFreight[];
    relatedReloadFreight?: TripRouteFreight | null;
  }>(),
  {
    relatedReloadFreight: null,
  },
);

const emits = defineEmits<{
  (e: "update:modelValue", routeFreights: TripRouteFreight[]): void;
}>();

const routeFreightModel = ref<TripRouteFreight>({ ...props.routeFreight });
const currentState = ref<OrderFreightState>(routeFreightModel.value.state);

const isPickUp = computed(() => {
  return props.routeStop.type === OrderStopType.pickup;
});

const isDeleted = computed(() => {
  return routeFreightModel.value.isDeleted;
});

const freightClass = computed<string>(() => {
  if (currentState.value === OrderFreightState.updated) {
    return "border-warning";
  }

  if (currentState.value === OrderFreightState.new) {
    return "border-success";
  }

  return "border-grey-lighter";
});

const canRestore = computed(() => {
  if (!routeFreightModel.value.isDeleted) {
    return false;
  }

  const relatedInitialFreight = props.freightsFromApi.find(
    (freightAPI) => freightAPI.id === routeFreightModel.value.id,
  );

  if (!relatedInitialFreight) {
    return true;
  }

  if (props.relatedReloadFreight) {
    return !props.relatedReloadFreight.isDeleted && !relatedInitialFreight.isDeleted;
  }

  return !relatedInitialFreight.isDeleted;
});

const isCreatePage = computed(() => {
  return route.params.id === "new";
});

function getFreightCharacteristics(freight: TripRouteFreight) {
  const { plannedQuantity, plannedWeight, type, stackable } = freight;

  return {
    plannedQuantity: Number(plannedQuantity),
    plannedWeight: Number(plannedWeight),
    type,
    stackable,
  };
}

function createFreightState(updatedFreight: TripRouteFreight): OrderFreightState {
  if (isCreatePage.value) {
    return updatedFreight.state;
  }

  const relatedInitialFreight = props.freightsFromApi.find((freight) => freight.id === updatedFreight.id);

  if (!relatedInitialFreight) {
    return updatedFreight.state;
  }

  const initialCharacteristics = getFreightCharacteristics(relatedInitialFreight);
  const updatedFreightCharacteristics = getFreightCharacteristics(updatedFreight);

  return JSON.stringify(initialCharacteristics) !== JSON.stringify(updatedFreightCharacteristics)
    ? OrderFreightState.updated
    : updatedFreight.state;
}

function deleteFreight() {
  const relatedFreightFromApi = props.freightsFromApi.find(
    (freightAPI) => freightAPI.id === routeFreightModel.value.id,
  );

  if (isCreatePage.value || !relatedFreightFromApi) {
    const updatedFreights = routeStopFreights.value.filter((freight) => freight.id !== routeFreightModel.value.id);

    emits("update:modelValue", updatedFreights);

    return;
  }

  routeFreightModel.value.isDeleted = true;
}

watch(
  routeFreightModel,
  (updatedRouteFreight) => {
    const updatedRouteStopFreights = routeStopFreights.value.map((freight) => {
      if (updatedRouteFreight.id === freight.id) {
        currentState.value = createFreightState(updatedRouteFreight);

        return {
          ...updatedRouteFreight,
          state: createFreightState(updatedRouteFreight),
        };
      }

      return freight;
    });

    emits("update:modelValue", updatedRouteStopFreights);
  },
  {
    deep: true,
  },
);
</script>

<template>
  <div :class="freightClass" class="px-4 pb-4 rounded-lg mt-4">
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <div class="mr-2">Route freight #{{ routeFreightModel.number }}:</div>
        <v-radio-group
          v-model="routeFreightModel.type"
          :disabled="routeFreightModel.isDeleted"
          :inline="true"
          color="primary"
          hide-details
        >
          <v-radio :value="freightTypePallets">
            <template #label>
              <span class="text-grey-darken-2 font-size-14">Pallets</span>
            </template>
          </v-radio>
          <v-radio :value="freightTypePieces" class="ml-1">
            <template #label>
              <span class="text-grey-darken-2 font-size-14">Pieces</span>
            </template>
          </v-radio>
        </v-radio-group>

        <route-freight-state
          v-if="!routeFreightModel.isDeleted && routeFreightModel.state !== OrderFreightState.normal"
          :state="routeFreightModel.state"
          class="ml-4"
        />

        <deleted-chip v-else-if="routeFreightModel.isDeleted" class="ml-4" />
      </div>
      <div class="d-flex align-center">
        <v-checkbox
          v-model="routeFreightModel.stackable"
          :disabled="routeFreightModel.isDeleted"
          :false-value="true"
          :true-value="false"
          class="pa-0 ma-0 stackable"
          color="primary"
          data-qa="stackable"
          hide-details
        >
          <template #label>
            <span class="text-grey-darken-2 font-size-14">Not stackable</span>
          </template>
        </v-checkbox>

        <v-btn
          v-if="isPickUp && !canRestore"
          :class="{
            'text-grey': isDeleted,
          }"
          :disabled="routeFreightModel.isDeleted"
          class="text-uppercase font-weight-600 text-danger font-size-12 ml-2"
          density="compact"
          variant="text"
          @click="deleteFreight"
        >
          <template #prepend>
            <v-icon icon="mdi-delete-outline" size="20"></v-icon>
          </template>

          Delete freight
        </v-btn>

        <v-btn
          v-if="isDeleted && canRestore"
          class="text-uppercase font-size-12 font-weight-600 ml-2"
          color="primary"
          variant="text"
          @click="routeFreightModel.isDeleted = false"
        >
          Restore freight
        </v-btn>
      </div>
    </div>

    <v-row class="mt-1">
      <v-col>
        <omni-text-field
          v-model="routeFreightModel.plannedQuantity"
          :disabled="routeFreightModel.isDeleted"
          :rules="[requiredValidator, positiveNumber]"
          data-qa="modal-planned-quantity"
          label="Planned quantity"
          min="0"
          required
          type="number"
        ></omni-text-field>
      </v-col>
      <v-col>
        <omni-text-field
          v-model="routeFreightModel.plannedWeight"
          :disabled="routeFreightModel.isDeleted"
          :rules="[requiredValidator, positiveNumber]"
          data-qa="modal-planned-weight"
          label="Planned total weight"
          min="0"
          required
          suffix="lbs"
          type="number"
        ></omni-text-field>
      </v-col>
    </v-row>

    <alert-message
      v-if="isDeleted"
      bg-color="indigo-lighten-5"
      border-color="none"
      class="mt-3"
      color="indigo"
      icon-color="indigo"
    >
      Freight#{{ routeFreightModel.number }} will be removed after re-dispatch confirmation
    </alert-message>
  </div>
</template>

<style lang="scss" scoped></style>
